import { useState, type FormEvent } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useCreateWorkspace } from '@fluxter/shared';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

export interface WorkspaceCreateModalProps {
  open: boolean;
  onClose: () => void;
}

export const WorkspaceCreateModal = ({ open, onClose }: WorkspaceCreateModalProps) => {
  const navigate = useNavigate();
  const create = useCreateWorkspace();
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setName('');
    setError(null);
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);

    try {
      const result = await create.mutateAsync({ name: name.trim() });
      const slug = (result as { data?: { attributes?: { slug?: string } } } | undefined)?.data?.attributes
        ?.slug;
      reset();
      onClose();
      if (slug) {
        await navigate({ to: '/w/$workspaceSlug', params: { workspaceSlug: slug } });
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not create workspace.');
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          reset();
          onClose();
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New workspace</DialogTitle>
          <DialogDescription>
            Workspaces silo projects, tasks, and notes. You become the owner of every workspace
            you create.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="workspace-name">Name</Label>
            <Input
              id="workspace-name"
              type="text"
              required
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Client Projects"
              maxLength={120}
            />
          </div>

          {error ? (
            <p className="text-destructive text-sm" role="alert">
              {error}
            </p>
          ) : null}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={create.isPending || !name.trim()}>
              {create.isPending ? 'Creating…' : 'Create workspace'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
